import type { ReactElement } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { policySubPath } from '@/lib/policyPaths';

interface CustomCapabilityRulesLinkProps {
  /** Number of configured custom rules, surfaced in the button label. */
  count: number;
  /** Read-only viewers get a "View" affordance instead of "Edit". */
  readOnly?: boolean;
}

/**
 * KIR-2691: the "Custom Capability Rules" row on the Extension Security policy.
 * Mirrors the Built-in Detection Catalog / AI Instruction Catalog affordance —
 * opens a dedicated full page (scope derived from the route) rather than an
 * inline editor.
 */
export function CustomCapabilityRulesLink({
  count,
  readOnly = false,
}: CustomCapabilityRulesLinkProps): ReactElement {
  const { organizationId } = useParams<{ organizationId?: string }>();

  const rulesPath = policySubPath(
    organizationId,
    'extension-security-custom-rules'
  );

  return (
    <div className="space-y-2">
      <p className="text-sm text-muted-foreground">
        Define your own rules that match VSCode extension manifest fields. Low
        or Info rules add capability metadata; Medium or High rules raise a
        security finding.
      </p>
      <Button asChild variant="outline">
        <Link to={rulesPath}>
          {readOnly ? 'View' : 'Edit'} Custom Rules ({count})
        </Link>
      </Button>
    </div>
  );
}
